import * as Sentry from '@sentry/react'
import { scrubSentryEvent, scrubSentryBreadcrumb, tagSentryAccount } from './lib/sentryUtils'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN_LIFF,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  tracesSampleRate: 0.2,
  integrations: [Sentry.browserTracingIntegration()],
  beforeSend: scrubSentryEvent,
  // LIFF-H4: scrub breadcrumbs at creation — axios request URLs/bodies carry
  // lease_code/auth_code; they must never enter the breadcrumb buffer.
  beforeBreadcrumb: scrubSentryBreadcrumb,
})

import { StrictMode, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import FlagProvider from '@unleash/proxy-client-react'
import { AccountProvider } from '@/contexts/AccountContext'
import { AuthProvider } from '@/contexts/AuthContext'
import { resolveLiffAccount, fetchSessionAccount, hasRefreshToken, refreshTokens, clearTokens } from '@/lib/api'
import { isNativePlatform, getNativePlatform } from '@/lib/native'
import { getBundledAccount } from '@/lib/bundledAccount'
import { applyAccountTheme } from '@/lib/accountTheme'
import type { AccountContext } from '@/types'
import App from './App'
import './i18n'
import './index.css'

const unleashConfig = {
  url: import.meta.env.VITE_UNLEASH_URL || 'https://unleash.kanoonth.com/api/frontend',
  clientKey: import.meta.env.VITE_UNLEASH_FRONTEND_KEY || '',
  appName: 'flowrent-liff',
  refreshInterval: 15,
}

// Shell classes at boot (mirrors web/src/main.tsx): `.native-shell` marks the
// Capacitor customer app; `.native-ios` additionally gates the safe-area-top
// floor in index.css — the iOS WKWebView renders edge-to-edge under the status
// bar but reports env(safe-area-inset-top) as 0, so CSS needs a real floor.
if (isNativePlatform()) {
  document.documentElement.classList.add('native-shell')
  if (getNativePlatform() === 'ios') {
    document.documentElement.classList.add('native-ios')
  }
}

if (import.meta.env.DEV) {
  import('vconsole').then(({ default: VConsole }) => {
    // eslint-disable-next-line no-new
    new VConsole()
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 1,
      retry: 1,
    },
  },
})

// eslint-disable-next-line react-refresh/only-export-components
function NotInLineScreen() {
  const { t } = useTranslation('common')

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-base-100 px-6 text-center">
      {/* LINE-style phone/app icon */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="mb-6 h-20 w-20 text-[#06C755]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"
        />
      </svg>
      <h1 className="mb-3 text-xl font-bold">{t('errors.not_in_line_title')}</h1>
      <p className="max-w-xs text-sm text-base-content/70">{t('errors.not_in_line_subtitle')}</p>
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
function Root() {
  const [account, setAccount] = useState<AccountContext | null>(null)
  const [accountLoading, setAccountLoading] = useState(true)
  const [accountError, setAccountError] = useState<string | null>(null)
  const [notInLine, setNotInLine] = useState(false)

  // EP-160 T160-102l: whichever path resolved the account (LIFF, session,
  // bundled, or post-login reload), tag Sentry once it is known.
  useEffect(() => {
    if (account) tagSentryAccount(account)
  }, [account])

  useEffect(() => {
    const init = async () => {
      // Native customer app (app/): there is no LINE context and no liff_id —
      // the lease-code login IS the account resolution. Skip the LIFF SDK
      // entirely; if a session survives (refresh token), resolve the account
      // from it now, otherwise leave it null and OtpPage resolves after login.
      if (isNativePlatform()) {
        // Enterprise white-label build: the account payload is baked into the
        // bundle, so resolution is instant and needs no network — the login
        // screen is fully branded before any request is possible.
        const bundled = getBundledAccount()
        if (bundled) {
          setAccount(bundled)
          applyAccountTheme(bundled)
          if (hasRefreshToken()) {
            try {
              await refreshTokens()
            } catch {
              clearTokens()
            }
          }
          setAccountLoading(false)
          return
        }

        if (hasRefreshToken()) {
          try {
            await refreshTokens()
            const data = await fetchSessionAccount()
            setAccount(data.account as AccountContext)
            applyAccountTheme(data.account as AccountContext)
          } catch {
            // Failed refresh = logged out; a failed account fetch just means a
            // themeless login screen. Neither is a boot error.
            clearTokens()
          }
        }
        setAccountLoading(false)
        return
      }

      try {
        // Initialize LIFF
        const liff = (await import('@line/liff')).default
        await liff.init({ liffId: import.meta.env.VITE_LIFF_ID || '__LIFF_ID__' })

        // Gate to LINE client only in production
        if (!liff.isInClient() && !import.meta.env.DEV) {
          setNotInLine(true)
          return
        }

        // Resolve account from LIFF ID
        const liffId = liff.id
        if (!liffId) {
          setAccountError('LIFF ID not available')
          return
        }

        const data = await resolveLiffAccount(liffId)
        setAccount(data.account as AccountContext)
        applyAccountTheme(data.account as AccountContext)
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 404) {
          setAccountError('LIFF app not configured for any account')
        } else {
          // In dev/non-LIFF environment, try to resolve with env LIFF ID
          const devLiffId = import.meta.env.VITE_LIFF_ID
          if (devLiffId) {
            try {
              const data = await resolveLiffAccount(devLiffId)
              setAccount(data.account as AccountContext)
              applyAccountTheme(data.account as AccountContext)
            } catch {
              setAccountError('Account not found for this LIFF ID')
            }
          } else {
            setAccountError('LIFF initialization failed')
          }
        }
      } finally {
        setAccountLoading(false)
      }
    }

    void init()
  }, [])

  if (notInLine) {
    return <NotInLineScreen />
  }

  // Post-login account resolution for the native shell: OtpPage calls this
  // after a successful lease-code login. In LINE the account is already
  // resolved pre-login, so this is a no-op there.
  const reloadAccount = async () => {
    if (!isNativePlatform()) return
    // White-label builds carry their account in the bundle — nothing to fetch.
    if (getBundledAccount()) return
    try {
      const data = await fetchSessionAccount()
      setAccount(data.account as AccountContext)
      applyAccountTheme(data.account as AccountContext)
    } catch {
      // Login succeeded; a failed account fetch degrades to default theming.
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AccountProvider account={account} isLoading={accountLoading} error={accountError} reloadAccount={reloadAccount}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </AccountProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <Sentry.ErrorBoundary fallback={<p>An unexpected error occurred.</p>}>
    <StrictMode>
      <FlagProvider config={unleashConfig}>
        <Root />
      </FlagProvider>
    </StrictMode>
  </Sentry.ErrorBoundary>
)
